import {
  formatPrice,
  KdsButton,
  KdsClipboardButton,
  KdsFocusableElement,
  KdsHeader,
  KdsText,
  KdsTooltip,
} from "@kazaar-platform/design-system";
import { RiInformationLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { isEmpty } from "lodash";
import { useNavigate } from "react-router-dom";

import { OrderItemConfigForm } from "@/components/OrderItemConfigForm/OrderItemConfigForm";
import { PageTemplate } from "@/components/PageTemplate";
import { type OrderStatusEnum } from "@/config/apiClient.types";
import { getOrderBadgeStatus } from "@/helpers/badgeStatusVariant";
import { getOrderBadgeStatusText } from "@/helpers/lang/getOrderBadgeStatusText";
import { useGetCurrency, useOrderItems, useToasts } from "@/hooks";
import { useScrollDetection } from "@/hooks/useScrollDetection";
import { userAtom } from "@/store";
import { type OrderItemDraftProps } from "./OrderItemDraft.types";

export const OrderItemDraft = ({
  orderItem,
  order,
  isLoading,
}: OrderItemDraftProps) => {
  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslate();
  const currency = useGetCurrency();
  const { add } = useToasts();
  const { ref: contentRef, isScrolled } = useScrollDetection();

  // Store
  const userAtomValue = useAtomValue(userAtom);

  // Query Hooks
  const { useDeleteOrderItem, useMarkDraftOrderItemAsInProgress } =
    useOrderItems();
  const { mutateAsync: mutateDeleteOrderItem, isPending: isDeletingOrderItem } =
    useDeleteOrderItem();
  const {
    mutateAsync: mutateSetOrderItemAsInProgress,
    isPending: isSettingOrderItemAsInProgress,
  } = useMarkDraftOrderItemAsInProgress();

  // Handle Delete Order Item
  const handleDeleteOrderItem = async () => {
    void navigate(-1);
    await mutateDeleteOrderItem({
      orderItemId: orderItem.id,
      orderId: order.id,
    });
  };

  // Handle Form Submit
  const onSubmit = async () => {
    try {
      const res = await mutateSetOrderItemAsInProgress({
        orderItemId: orderItem.id,
        orderId: order.id,
      });

      if (res.status === 200 || res.status === 201) {
        add({
          state: "success",
          title: t("orderItemDraft.toast.success"),
        });
      }
    } catch {
      add({
        state: "error",
        title: t("orderItemDraft.toast.error"),
      });
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <PageTemplate>
      <PageTemplate.Header>
        <KdsHeader
          backButtonProps={{
            onPress: () => void handleDeleteOrderItem(),
          }}
          badgeProps={{
            state: getOrderBadgeStatus(order.status as OrderStatusEnum),
            size: "sm",
            children: getOrderBadgeStatusText(order.status),
          }}
          isScrolled={isScrolled}
          metadatas={
            order.referenceId
              ? [
                  <KdsClipboardButton
                    key={order.referenceId}
                    text={order.referenceId}
                    displayToast={() =>
                      add({
                        key: order.referenceId,
                        state: "reverse",
                        title: t("toast.clipboard.orderId"),
                      })
                    }
                  />,
                ]
              : []
          }
          title={order.name}
        />
      </PageTemplate.Header>

      <PageTemplate.Content
        className="flex-col gap-sp-lg"
        contentRef={contentRef}
      >
        <div className="flex h-full flex-col gap-sp-xl lg:flex-row-reverse lg:flex-nowrap">
          {/* Right Column */}
          <div className="flex h-fit w-full flex-col gap-sp-xl lg:w-1/3 lg:max-w-[400px]">
            <div className="flex w-full flex-col gap-sp-md rounded-lg bg-white p-sp-xl">
              {/* Price / Quote */}
              <div className="flex flex-col">
                <KdsText color="text-bodyvariant">
                  {t("orderItemDraft.priceEstimate")}
                </KdsText>

                <div className="flex items-center gap-sp-xs">
                  <KdsText size="title-lg" dataCy="draftOrderPageTotalPrice">
                    {!orderItem.price
                      ? t("toQuote")
                      : formatPrice({
                          price: orderItem.price,
                          locale: userAtomValue?.lang,
                          currency: currency,
                        })}
                  </KdsText>

                  {!orderItem.price && (
                    <KdsTooltip alt={t("order.orderDraft.quoteInfo")}>
                      <KdsFocusableElement>
                        <RiInformationLine className="h-6 min-h-6 w-6 min-w-6 text-text-body" />
                      </KdsFocusableElement>
                    </KdsTooltip>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-sp-md">
                {/* Validate Order Button */}
                <KdsTooltip
                  alt={t("orderItemIssuesGeneric", { count: 1 })}
                  placement="bottom"
                  isDisabled={isEmpty(orderItem.orderItemIssues)}
                >
                  <KdsButton
                    disabled={!isEmpty(orderItem.orderItemIssues)}
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    dataCy="validateOrderButton"
                    isLoading={isSettingOrderItemAsInProgress}
                    onPress={() => void onSubmit()}
                  >
                    {t("buttons.addProductToOrder")}
                  </KdsButton>
                </KdsTooltip>

                {/* Cancel Button */}
                <KdsButton
                  isLoading={isDeletingOrderItem}
                  variant="outlined"
                  color="error"
                  size="large"
                  fullWidth
                  onPress={() => void handleDeleteOrderItem()}
                >
                  {t("buttons.cancel")}
                </KdsButton>
              </div>
            </div>
          </div>

          {/* Left Column */}
          <div className="flex flex-1 flex-col lg:w-2/3">
            <div className="flex flex-col gap-4">
              <div className="mb-12 flex flex-col gap-sp-xs rounded-lg bg-elevation-first p-sp-xl">
                {/* Order Item Header */}
                <div className="flex flex-col border-b pb-sp-lg">
                  <KdsText size="title-md">{t("orderItemDraft.title")}</KdsText>
                  <KdsText color="text-bodyvariant">
                    {t("orderItemDraft.subtitle")}
                  </KdsText>
                </div>

                {/* Order Item Config Form */}
                <OrderItemConfigForm
                  key={orderItem.id}
                  orderItem={orderItem}
                  order={order}
                  hasAutoSave
                  className="px-sp-md py-sp-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </PageTemplate.Content>
    </PageTemplate>
  );
};
